import React, { useState } from 'react';
import { cn } from '@/utils/cn';

// Main Avatar container
export interface AvatarProps {
  className?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, children, size = 'md', ...props }, ref) => {
    const sizeClasses = {
      sm: 'h-8 w-8',
      md: 'h-10 w-10',
      lg: 'h-12 w-12',
      xl: 'h-16 w-16',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex shrink-0 overflow-hidden rounded-full bg-gray-100',
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

// Avatar Image component
export interface AvatarImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  className?: string;
  src?: string;
  alt?: string;
  onLoadingStatusChange?: (status: 'idle' | 'loading' | 'loaded' | 'error') => void;
}

export const AvatarImage = React.forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, src, alt, onLoadingStatusChange, ...props }, ref) => {
    const [_, setLoadingStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

    const handleLoad = () => {
      setLoadingStatus('loaded');
      onLoadingStatusChange?.('loaded');
    };

    const handleError = () => {
      setLoadingStatus('error');
      onLoadingStatusChange?.('error');
    };

    const handleLoadStart = () => {
      setLoadingStatus('loading');
      onLoadingStatusChange?.('loading');
    };

    // Don't render if there's no src or if there was an error loading
    if (!src || loadingStatus === 'error') {
      return null;
    }

    return (
      <img
        ref={ref}
        className={cn('aspect-square h-full w-full object-cover', className)}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        onLoadStart={handleLoadStart}
        {...props}
      />
    );
  }
);

AvatarImage.displayName = 'AvatarImage';

// Avatar Fallback component
export interface AvatarFallbackProps {
  className?: string;
  children: React.ReactNode;
  delayMs?: number;
}

export const AvatarFallback = React.forwardRef<HTMLDivElement, AvatarFallbackProps>(
  ({ className, children, delayMs = 0, ...props }, ref) => {
    const [_, setCanRender] = useState(delayMs === 0);

    React.useEffect(() => {
      if (delayMs > 0) {
        const timer = setTimeout(() => {
          setCanRender(true);
        }, delayMs);

        return () => clearTimeout(timer);
      }
    }, [delayMs]);

    if (!canRender) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'flex h-full w-full items-center justify-center rounded-full bg-gray-200 text-gray-600 font-medium text-sm',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

AvatarFallback.displayName = 'AvatarFallback';

// For backwards compatibility, export AvatarContent as alias for AvatarImage
export const AvatarContent = AvatarImage;
